import { type JSX, useCallback, useEffect } from 'react';
import type { UIMatch } from 'react-router';
import { useMatches, useSearchParams } from 'react-router';

import type { ItemType } from '~/components/common/SelectCheckbox';
import SelectCheckbox from '~/components/common/SelectCheckbox';
import MonthRangePicker from '~/components/date/MonthRangePicker/MonthRangePicker';
import type { SelectedDateRange } from '~/components/date/MonthRangePicker/types';
import {
  dateToDateString,
  getDefaultMonthRange,
  saveMonthRangeToStorage,
} from '~/components/utils/dateUtils';
import type { GlobalFiltersChangeOptions } from '~/contexts/globalFilterContext';
import { GlobalFilterContext, useGlobalFilterContext } from '~/contexts/globalFilterContext';
import { useFeatureFlags } from '~/utils/featureFlags';

type FilterName =
  | 'MonthRangePicker'
  | 'portfolioDropDown'
  | 'serviceProviderDropDown'
  | 'regionDropDown';

const filterToSearchParamMap = {
  portfolios: 'portfolio',
  serviceProviders: 'service_provider',
  regions: 'region',
} as const;

type Filter = {
  name: FilterName;
  component: JSX.Element;
};

type GetMonthRangePickerFilter = {
  value: SelectedDateRange;
  eventHandler: (selectedRage: SelectedDateRange) => void;
  disabled?: boolean;
};

const getMonthRangePickerFilter = ({
  value,
  eventHandler,
  disabled,
}: GetMonthRangePickerFilter): Filter => {
  return {
    name: 'MonthRangePicker',
    component: (
      <MonthRangePicker
        placeHolder="Period"
        onSelect={eventHandler}
        value={value}
        disabled={disabled}
      />
    ),
  };
};

type GetDropDownFilter = {
  name: FilterName;
  label: string;
  options: ItemType[];
  value?: string[];
  eventHandler: (selectedItems: string[]) => void;
  disabled?: boolean;
  singleSelect?: boolean;
  emptyLabel?: string;
};

type RemoveFiltersType = {
  [key: string]: string[];
};

// Per control, the routes that can't narrow by it: dropped from the band outright,
// or drawn disabled on the routes in `routesShowingInertFilters`.
//
// A property page is listed under both its ids — `property-detail` (/portfolio/:id)
// is a redirect-only route to it, `properties-property-detail` (/properties/:id)
// serves it directly.
//
// Reports keeps Portfolio and Region: the frozen report was always run for a
// chosen set of portfolios, and without that the live report covers a whole
// customer account and matches nothing a customer already has. It drops the other
// two because the report cannot honour them. Period would fight the report's own
// windows (a reporting month and a trailing twelve), which it states in its
// header. Service provider would blank four blocks: the callback-rate trend and
// both KPI tables divide by every elevator at a property, and a vendor services
// some of a property's cars, not all, so there is no correct denominator to
// narrow them to.
const routesToFilterForFilterName: RemoveFiltersType = {
  filterPortfolios: [
    'property-detail',
    'properties-property-detail',
    'invoice-detail',
    'contract-detail',
    'elevator-detail',
    'tools',
    'ai-extracted',
    'insights',
    'admin',
    'task-detail',
  ],
  filterServiceProviders: [
    'property-detail',
    'properties-property-detail',
    'invoice-detail',
    'contract-detail',
    'elevator-detail',
    'tools',
    'ai-extracted',
    'insights',
    'reports',
    'admin',
    'inbox',
    'tasks',
    'task-detail',
  ],
  filterRegions: [
    'property-detail',
    'properties-property-detail',
    'invoice-detail',
    'contract-detail',
    'elevator-detail',
    'tools',
    'ai-extracted',
    'insights',
    'admin',
    'task-detail',
  ],
  filterPeriod: [
    'invoice-detail',
    'elevator-detail',
    'tools',
    'ai-extracted',
    'insights',
    'reports',
    'admin',
    'inbox',
    'contracts',
    'compliance',
    'task-detail',
  ],
};

/**
 * Routes that grey out the controls they can't narrow by rather than dropping them,
 * so the band keeps its height and every control its place.
 *
 * An inert control carries no selection either — see `INERT_SELECTION` /
 * `INERT_DATE_RANGE` below.
 */
const routesShowingInertFilters = [
  'ai-extracted',
  'tasks',
  'reports',
  'contracts',
  'compliance',
  'inbox',
  // Still narrows by Period, so the band stays up either way; greying out the three
  // it can't honour keeps them in their usual places beside the live picker.
  'properties-property-detail',
];

/**
 * Detail pages that drop the band outright, overriding the inert list above. The
 * lists are matched against every route in the tree, so a detail page under a list
 * that draws inert controls would otherwise inherit them.
 */
const routesNeverShowingInertFilters = ['contract-detail', 'elevator-detail', 'task-detail'];

const INERT_SELECTION: string[] = [];
const INERT_DATE_RANGE: SelectedDateRange = { start: null, end: null };

const getDropDownFilter = ({
  name,
  label,
  options,
  eventHandler,
  value,
  disabled,
  singleSelect,
  emptyLabel,
}: GetDropDownFilter): Filter => {
  return {
    name,
    component: (
      <SelectCheckbox
        label={label}
        options={options}
        onSelect={eventHandler}
        value={value}
        disabled={disabled}
        singleSelect={singleSelect}
        emptyLabel={emptyLabel}
      />
    ),
  };
};

type GlobalFilterChip = {
  key: string;
  groupLabel: string;
  label: string;
  onDelete: () => void;
};

export type GlobalFilters = {
  filters: Filter[];
  /** Period is deliberately absent: it always holds a range, so a chip for it would be permanent furniture. */
  chips: GlobalFilterChip[];
  clearAll: () => void;
  canClear: boolean;
};

/** Everything or nothing — the resting state, mirroring `SelectCheckbox`, which renders both as "All". */
const showsAll = (selected: string[], options: ItemType[]) =>
  selected.length === 0 || selected.length === options.length;

/**
 * Whether the route currently matched greys out the controls it can't honour rather
 * than dropping them. Exported for tests.
 */
export function showsInertFilters(matches: Pick<UIMatch, 'id'>[]) {
  if (matches.some((match) => routesNeverShowingInertFilters.includes(match.id))) return false;
  return matches.some((match) => routesShowingInertFilters.includes(match.id));
}

// Shared with the report body, which points the reader back at this picker by
// name when no portfolio is selected.
export const SELECT_A_PORTFOLIO = 'Select a portfolio';

export const useGlobalFilters = (): GlobalFilters => {
  const matches = useMatches();
  const [, setSearchParams] = useSearchParams();
  const filterEls: Filter[] = [];
  const chips: GlobalFilterChip[] = [];
  const { options, selectedFilters, onGlobalFiltersChange } = useGlobalFilterContext();

  const showsInert = showsInertFilters(matches);

  const isInert = (filterName: string) => showsInert && !showFilter(filterName, matches);

  // The live report covers one portfolio at a time — a report spanning several
  // is not a report any customer has ever been sent — so on that route the
  // portfolio picker takes a single selection instead of the app-wide
  // multi-select. Gated on the relaunch flag: without it /reports is the legacy
  // archive, which never reads the portfolio filter, and narrowing the picker
  // there would cost those users their selection for no gain.
  const featureFlags = useFeatureFlags();
  const singlePortfolio =
    featureFlags.reportsRelaunch === true && matches.some((match) => match.id === 'reports');

  const onChange = useCallback(
    (
      filters: Partial<GlobalFilterContext['selectedFilters']>,
      changeOptions?: GlobalFiltersChangeOptions,
    ) => {
      setSearchParams((prev) => {
        const searchParamsClone = new URLSearchParams(prev);

        for (const [key, value] of Object.entries(filters)) {
          searchParamsClone.delete(key);

          if (Array.isArray(value)) {
            const searchParamName =
              filterToSearchParamMap[key as keyof typeof filterToSearchParamMap] ?? key;
            searchParamsClone.delete(searchParamName);

            for (const item of value) {
              searchParamsClone.append(searchParamName, item);
            }
          } else if (key === 'date') {
            searchParamsClone.set('start_date', dateToDateString(value.start));
            searchParamsClone.set('end_date', dateToDateString(value.end));
          } else {
            // Do nothing
          }
        }

        return searchParamsClone;
      });

      onGlobalFiltersChange(
        {
          date: selectedFilters.date,
          portfolios: selectedFilters.portfolios,
          serviceProviders: selectedFilters.serviceProviders,
          regions: selectedFilters.regions,
          ...filters,
        },
        changeOptions,
      );
    },
    [onGlobalFiltersChange, selectedFilters, setSearchParams],
  );

  // Reconcile a selection the route cannot express. Several portfolios carried
  // in from another page are cleared rather than trimmed to one: keeping an
  // arbitrary one would pick the reader's scope for them without saying so, and
  // the empty prompt is the same state a first-time visitor lands on. An account
  // that can reach only one portfolio has no choice to make, so it is made.
  const selectedPortfolios = selectedFilters.portfolios;
  const portfolioOptions = options.portfolios;
  useEffect(() => {
    if (!singlePortfolio) return;
    // Silent: neither branch is a filter the reader applied, and counting them
    // would put a filter event on every Reports visit for anyone who holds a
    // single portfolio.
    if (selectedPortfolios.length > 1) {
      onChange({ portfolios: [] }, { silent: true });
    } else if (selectedPortfolios.length === 0 && portfolioOptions.length === 1) {
      onChange({ portfolios: [portfolioOptions[0].id] }, { silent: true });
    }
  }, [singlePortfolio, selectedPortfolios, portfolioOptions, onChange]);

  const monthRangePickerFilterHandler = useCallback(
    (selectedRange: SelectedDateRange) => {
      const date =
        selectedRange.start && selectedRange.end ? selectedRange : getDefaultMonthRange();
      saveMonthRangeToStorage(date);
      onChange({ date });
    },
    [onChange],
  );

  const periodInert = isInert('filterPeriod');
  const showPeriod = showFilter('filterPeriod', matches) || periodInert;
  if (showPeriod) {
    const monthRangePickerFilter = getMonthRangePickerFilter({
      value: periodInert ? INERT_DATE_RANGE : selectedFilters.date,
      eventHandler: monthRangePickerFilterHandler,
      disabled: periodInert,
    });
    filterEls.push(monthRangePickerFilter);
  }

  // Portfolios
  const portfolioFilterHandler = useCallback(
    (selectedItems: string[]) => {
      onChange({
        portfolios: selectedItems,
      });
    },
    [onChange],
  );

  const portfoliosInert = isInert('filterPortfolios');
  const showPortfolios = showFilter('filterPortfolios', matches) || portfoliosInert;
  if (showPortfolios) {
    const filterPortfolios = portfoliosInert
      ? INERT_SELECTION
      : getFilterItem(options.portfolios, selectedFilters.portfolios);
    const portfolioFilterPros = {
      options: options.portfolios,
      value: filterPortfolios.length ? filterPortfolios : INERT_SELECTION,
    };

    const portfolioFilter = getDropDownFilter({
      name: 'portfolioDropDown',
      label: 'Portfolio',
      ...portfolioFilterPros,
      eventHandler: portfolioFilterHandler,
      disabled: portfoliosInert,
      singleSelect: singlePortfolio,
      // "All" is the wrong word for a picker whose empty state is a prompt, not
      // a scope: it reads as "every portfolio", which is exactly what the report
      // will not show.
      emptyLabel: singlePortfolio ? SELECT_A_PORTFOLIO : undefined,
    });
    filterEls.push(portfolioFilter);
    chips.push(
      ...toChips(
        'portfolio',
        'Portfolio',
        filterPortfolios,
        options.portfolios,
        portfolioFilterHandler,
      ),
    );
  }

  // Service Provider
  const serviceProviderFilterHandler = useCallback(
    (selectedItems: string[]) => {
      onChange({
        serviceProviders: selectedItems,
      });
    },
    [onChange],
  );

  const serviceProvidersInert = isInert('filterServiceProviders');
  const showServiceProviders =
    showFilter('filterServiceProviders', matches) || serviceProvidersInert;
  if (showServiceProviders) {
    const filterServiceProviders = serviceProvidersInert
      ? INERT_SELECTION
      : getFilterItem(options.serviceProviders, selectedFilters.serviceProviders);
    const serviceProviderFilterProps = {
      options: options.serviceProviders,
      value: filterServiceProviders.length ? filterServiceProviders : INERT_SELECTION,
    };

    const serviceProviderFilter = getDropDownFilter({
      name: 'serviceProviderDropDown',
      label: 'Service Provider',
      ...serviceProviderFilterProps,
      eventHandler: serviceProviderFilterHandler,
      disabled: serviceProvidersInert,
    });
    filterEls.push(serviceProviderFilter);
    chips.push(
      ...toChips(
        'serviceProvider',
        'Service Provider',
        filterServiceProviders,
        options.serviceProviders,
        serviceProviderFilterHandler,
      ),
    );
  }

  // Regions
  const regionFilterHandler = useCallback(
    (selectedItems: string[]) => {
      onChange({
        regions: selectedItems,
      });
    },
    [onChange],
  );

  const regionsInert = isInert('filterRegions');
  const showRegions = showFilter('filterRegions', matches) || regionsInert;
  if (showRegions) {
    const filterRegions = regionsInert
      ? INERT_SELECTION
      : getFilterItem(options.regions, selectedFilters.regions);
    const regionFilterProps = {
      options: options.regions,
      value: filterRegions.length ? filterRegions : INERT_SELECTION,
    };

    const regionFilter = getDropDownFilter({
      name: 'regionDropDown',
      label: 'Region',
      ...regionFilterProps,
      eventHandler: regionFilterHandler,
      disabled: regionsInert,
    });
    filterEls.push(regionFilter);
    chips.push(...toChips('region', 'Region', filterRegions, options.regions, regionFilterHandler));
  }

  // Clears only what this route narrows by: selections persist across pages, so
  // clearing a control the user can't act on here would undo a filter they set
  // somewhere else. Period returns to its default — every page behind it queries a range.
  const clearAll = useCallback(() => {
    const next: Partial<GlobalFilterContext['selectedFilters']> = {};
    if (showPortfolios && !portfoliosInert) next.portfolios = [];
    if (showServiceProviders && !serviceProvidersInert) next.serviceProviders = [];
    if (showRegions && !regionsInert) next.regions = [];
    if (showPeriod && !periodInert) {
      const date = getDefaultMonthRange();
      saveMonthRangeToStorage(date);
      next.date = date;
    }
    onChange(next);
  }, [
    onChange,
    periodInert,
    portfoliosInert,
    regionsInert,
    serviceProvidersInert,
    showPeriod,
    showPortfolios,
    showRegions,
    showServiceProviders,
  ]);

  // Period doesn't count as "something to clear": a range is always set, so a
  // non-default month would light up Clear all without anything being narrowed.
  return {
    filters: filterEls,
    chips,
    clearAll,
    canClear: chips.length > 0,
  };
};

function toChips(
  group: string,
  groupLabel: string,
  selected: string[],
  options: ItemType[],
  onSelect: (next: string[]) => void,
): GlobalFilterChip[] {
  if (showsAll(selected, options)) return [];

  return selected.map((id) => ({
    key: `${group}:${id}`,
    groupLabel,
    label: options.find((option) => option.id === id)?.label ?? id,
    onDelete: () => onSelect(selected.filter((selectedId) => selectedId !== id)),
  }));
}

function getFilterItem(items: { id: string }[], queryParams: string[]) {
  return items.map((p) => p.id).filter((id) => queryParams.includes(id));
}

/** Whether one control belongs on the route currently matched. Exported for tests. */
export function showFilter(filterName: string, matches: Pick<UIMatch, 'id'>[]) {
  const routesToFilter = routesToFilterForFilterName[filterName];

  return !matches.some((match) => routesToFilter.includes(match.id));
}
